import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
} from '@cartcloser/ui';
import { inviteMember, listMembers, revokeInvite, type InviteRole } from '../lib/api.js';

export const Route = createFileRoute('/admin/$slug/members')({
  component: MembersPage,
});

function MembersPage() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['members', slug], queryFn: () => listMembers(slug) });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('member');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['members', slug] });

  const invite = useMutation({
    mutationFn: () => inviteMember(slug, { email, role }),
    onSuccess: async () => {
      setEmail('');
      setError(null);
      await invalidate();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not send invite'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeInvite(slug, id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Team</h1>
        <p className="text-sm text-muted-foreground">Invite teammates and manage their access.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Invite a teammate</CardTitle>
          <CardDescription>
            They’ll get an email link. Admins can manage the store; members have read access.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              invite.mutate();
            }}
          >
            <FormField label="Email" className="flex-1" required>
              {({ id }) => (
                <Input
                  id={id}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@store.com"
                  required
                />
              )}
            </FormField>
            <FormField label="Role">
              {({ id }) => (
                <select
                  id={id}
                  value={role}
                  onChange={(e) => setRole(e.target.value as InviteRole)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              )}
            </FormField>
            <Button type="submit" loading={invite.isPending}>
              Send invite
            </Button>
          </form>
          {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            query.data?.members.map((m) => (
              <div
                key={m.userId}
                className="flex items-center justify-between rounded-md border border-border p-3 text-sm"
              >
                <div>
                  <p className="font-medium">{m.name || m.email}</p>
                  <p className="text-xs text-muted-foreground">{m.email}</p>
                </div>
                <Badge variant={m.role === 'owner' ? 'default' : 'secondary'}>{m.role}</Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {query.data && query.data.invitations.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Pending invitations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {query.data.invitations.map((i) => (
              <div
                key={i.id}
                className="flex items-center justify-between rounded-md border border-border p-3 text-sm"
              >
                <div>
                  <p className="font-medium">{i.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {i.role} · expires {new Date(i.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => revoke.mutate(i.id)}>
                  Revoke
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
